import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "../../../api-client/APIClient";

export enum MergeFlagFeature {
  MERGE_FLAG_ENABLE_DATA_WAREHOUSE = "MERGE_FLAG_ENABLE_DATA_WAREHOUSE", // TODO: remove https://app.asana.com/0/1205550558375762/1205671730434201/f
  MERGE_FLAG_SHOW_DEPRECATED_SUNSET_ITEMS = "MERGE_FLAG_SHOW_DEPRECATED_SUNSET_ITEMS", // never remove!
  ENABLE_SELECTIVE_SYNC = "ENABLE_SELECTIVE_SYNC", // TODO: remove https://app.asana.com/0/1206661594236943/1206670899981051/f
  MERGE_FLAG_ENABLE_COMMON_MODEL_SCOPE_DEPENDENCIES = "MERGE_FLAG_ENABLE_COMMON_MODEL_SCOPE_DEPENDENCIES", // TODO: remove https://app.asana.com/0/1205550558375762/1207232599957511/f
  MERGE_FLAG_ENABLE_ENDPOINT_USAGE = "MERGE_FLAG_ENABLE_ENDPOINT_USAGE", // TODO: remove https://app.asana.com/0/1207602234244110/1207847435589277/f
  MERGE_FLAG_ENABLE_DEFAULT_DATA = "MERGE_FLAG_ENABLE_DEFAULT_DATA", // TODO: remove https://app.asana.com/0/1207729296746643/1208334933287285/f
  MERGE_FLAG_FILE_PICKER_DASHBOARD = "MERGE_FLAG_FILE_PICKER_DASHBOARD",
  MERGE_FLAG_DISABLE_ORG_SCOPE_EDITING = "MERGE_FLAG_DISABLE_ORG_SCOPE_EDITING", // Disables ability to edit org-wide scope/integration partnership settings for specific orgs
  MERGE_FLAG_ENABLE_PLA_THRESHOLD_BANNERS = "MERGE_FLAG_ENABLE_PLA_THRESHOLD_BANNERS",
  MERGE_FLAG_ENABLE_MICROSOFT_SENSITIVITY_LABELS = "MERGE_FLAG_ENABLE_MICROSOFT_SENSITIVITY_LABELS",
  MERGE_FLAG_ENABLE_SHAREPOINT_ONEDRIVE_SYNC = "MERGE_FLAG_ENABLE_SHAREPOINT_ONEDRIVE_SYNC",
  MERGE_FLAG_FRIGADE_ASSISTANT = "MERGE_FLAG_FRIGADE_ASSISTANT",
  // Opt-out list for the Frigade Assistant: orgs on this flag never get the
  // Assistant, even while MERGE_FLAG_FRIGADE_ASSISTANT is on for everyone.
  // Read through useFrigadeAssistantEnabled rather than directly.
  MERGE_FLAG_FRIGADE_ASSISTANT_EXCLUDED = "MERGE_FLAG_FRIGADE_ASSISTANT_EXCLUDED",
  // TODO @shensi: remove once the redesign is the only Linked Accounts table https://app.asana.com/1/1174208460831550/project/1200046105855900/task/1218441786265000?focus=true
  MERGE_FLAG_LINKED_ACCOUNTS_TABLE_REDESIGN = "MERGE_FLAG_LINKED_ACCOUNTS_TABLE_REDESIGN",
  MERGE_FLAG_LINKED_ACCOUNTS_DATA_FRESH_AS_OF = "MERGE_FLAG_LINKED_ACCOUNTS_DATA_FRESH_AS_OF",
}

export type UseMergeFlagProps = {
  feature: MergeFlagFeature;
  organizationId?: string;
  linkedAccountId?: string;
  integrationId?: string;
};

/**
 * Hook for checking if merge flag is enabled. Requires one (and only one)
 * of organizationId, linkedAccountId, or integrationId
 *
 * If you just want to hide UI can use `<MergeFlagWrapper />`
 */
export const useMergeFlag = ({
  feature,
  linkedAccountId,
  organizationId,
  integrationId,
}: UseMergeFlagProps) => {
  // state
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);
  const [enabledForLinkedAccount, setEnabledForLinkedAccount] = useState<boolean | undefined>(
    undefined,
  );
  const [enabledForOrganization, setEnabledForOrganization] = useState<boolean | undefined>(
    undefined,
  );
  const [enabledForIntegration, setEnabledForIntegration] = useState<boolean | undefined>(
    undefined,
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  // event handlers
  const loadMergeFlagPermissions = useCallback(() => {
    fetchWithAuth({
      path: `integrations/merge-flags/${feature}/enabled`,
      method: "POST",
      body: {
        organizationId,
        integrationId,
        linkedAccountId,
      },
      onResponse: (res: {
        feature_enabled_for_organization: boolean;
        feature_enabled_for_integration: boolean;
        feature_enabled_for_linked_account: boolean;
      }) => {
        if (
          (organizationId && res.feature_enabled_for_organization) ||
          (integrationId && res.feature_enabled_for_integration) ||
          (linkedAccountId && res.feature_enabled_for_linked_account)
        ) {
          setEnabled(true);
        } else {
          setEnabled(false);
        }
        setEnabledForLinkedAccount(res.feature_enabled_for_linked_account ?? undefined);
        setEnabledForOrganization(res.feature_enabled_for_organization ?? undefined);
        setEnabledForIntegration(res.feature_enabled_for_integration ?? undefined);
        setHasInitialized(true);
      },
      onError: (err) => {
        console.error(err);
        setHasInitialized(true);
      },
    });
  }, [integrationId, linkedAccountId, organizationId, feature]);

  // effects
  // initialize
  useEffect(() => {
    loadMergeFlagPermissions();
  }, [loadMergeFlagPermissions]);

  return {
    hasInitialized,
    enabled,
    enabledForLinkedAccount,
    enabledForOrganization,
    enabledForIntegration,
  };
};
